import { Alert, Box, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { LoadingSpinner } from '@verantos-dev/shared-components';

import type { AgentAllocationPreviewStatus } from '../../hooks/useAnnotationAgentAllocation';
import type { AnnotationAgentAllocationPreviewResponse } from '../../types/response';

// TODO: cleanup with cp_annotation_createproject_agent ff

interface AgentAllocationSummaryProps {
  status: AgentAllocationPreviewStatus;
  preview: AnnotationAgentAllocationPreviewResponse | null;
  /** Custom mode: the prototype's Custom copy; the per-human split is in the Custom table. */
  isCustom: boolean;
}

/**
 * Step 6 with the annotation agent on: how the 2n annotation assignments split between the
 * agent and the humans. A failed count, or no encounter to annotate, is shown as an error
 * because it keeps Next disabled.
 */
export const AgentAllocationSummary = ({
  status,
  preview,
  isCustom,
}: AgentAllocationSummaryProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  if (status === 'loading') {
    return (
      <Box
        role="status"
        aria-label={formatMessage({ id: 'createProjectWizard.step.allocation.agent.loading' })}
        sx={{ display: 'flex', justifyContent: 'center', py: theme.spacing(4) }}
      >
        <LoadingSpinner id="agentAllocationPreview" />
      </Box>
    );
  }
  if (status === 'error' || !preview) {
    return (
      <Alert severity="error">
        {formatMessage({ id: 'createProjectWizard.step.allocation.agent.error' })}
      </Alert>
    );
  }
  if (preview.encounterCount === 0) {
    return (
      <Alert severity="error">
        {formatMessage({ id: 'createProjectWizard.step.allocation.agent.noEncounters' })}
      </Alert>
    );
  }

  const encounterCount = preview.encounterCount;
  if (isCustom) {
    return (
      <Alert severity="info" id="agent-allocation-summary">
        <Typography variant="body2" sx={{ mb: theme.spacing(1), fontWeight: 'medium' }}>
          {formatMessage(
            { id: 'createProjectWizard.step.allocation.agent.custom.totalEncounters' },
            { encounterCount, assignmentCount: encounterCount * 2 }
          )}
        </Typography>
        <Typography variant="body2">
          {formatMessage({ id: 'createProjectWizard.step.allocation.agent.custom.description' })}
        </Typography>
      </Alert>
    );
  }
  return (
    <Alert severity="info" id="agent-allocation-summary">
      <Typography variant="body2">
        {formatMessage({ id: 'createProjectWizard.step.allocation.agent.description' })}
      </Typography>
      <Typography variant="body2" sx={{ mt: theme.spacing(2), fontWeight: 'medium' }}>
        {formatMessage(
          { id: 'createProjectWizard.step.allocation.agent.totalAssignments' },
          { encounterCount, assignmentCount: encounterCount * 2 }
        )}
      </Typography>
      <Typography variant="body2">
        {formatMessage(
          { id: 'createProjectWizard.step.allocation.agent.split' },
          {
            agentEncounterCount: preview.agent.encounterCount,
            humanEncounterCount: encounterCount,
          }
        )}
      </Typography>
    </Alert>
  );
};
